import { cn } from "@newxsystems/shared";
import type {
  MegaMenuAction,
  MegaMenuDropdown,
  MegaMenuGroup,
  MegaMenuGroupListItem,
  MegaMenuItem,
  MegaMenuLink,
} from "./MegaMenuBlock.props";

export type KaajPathMatch = "location" | "page" | undefined;

const normalizeKaajPath = (value: string | null | undefined): string | undefined => {
  const pathname = value?.trim().split(/[?#]/u, 1)[0];
  if (!pathname?.startsWith("/")) return undefined;

  const normalized = pathname.replace(/\/{2,}/gu, "/").replace(/\/+$/u, "");
  return normalized || "/";
};

export const getKaajPathMatch = (
  currentPath: string | null | undefined,
  href: string | null | undefined,
): KaajPathMatch => {
  const current = normalizeKaajPath(currentPath);
  const destination = normalizeKaajPath(href);
  if (!current || !destination) return undefined;
  if (current === destination) return "page";
  if (destination !== "/" && current.startsWith(`${destination}/`)) return "location";
  return undefined;
};

export const getItemKind = (item: MegaMenuItem) => {
  const itemKind = item.blockType;

  if (itemKind === "megaMenuDropdown") return "dropdown";
  if (itemKind === "megaMenuGroup") return "group";
  if (itemKind === "megaMenuLink") return "link";

  return itemKind;
};

export const getLabel = (
  item: MegaMenuLink | MegaMenuDropdown | MegaMenuGroup | MegaMenuGroupListItem | MegaMenuAction,
) => {
  if ("header" in item) return item.header;

  const itemKind = getItemKind(item as MegaMenuItem);

  if (itemKind === "dropdown" || itemKind === "group") {
    return (item as MegaMenuDropdown | MegaMenuGroup).label;
  }

  return (item as MegaMenuLink | MegaMenuAction).text;
};

export const itemKey = (
  prefix: string,
  index: number,
  item: { href?: string; text?: string; label?: string; header?: string },
) => `${prefix}-${index}-${item.href ?? item.text ?? item.label ?? item.header ?? "item"}`;

export const actionClassName = (style?: MegaMenuAction["style"]) => {
  const baseClassName =
    "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition-[background-color,border-color,box-shadow,transform] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF] motion-reduce:transition-none";

  switch (style) {
    case "secondary":
      return cn(
        baseClassName,
        "border border-[#D9E1FF] bg-[#F6F1FF] text-[#2144FF] hover:border-[#2144FF] hover:bg-white active:bg-[#EEF2FF]",
      );
    case "outline":
      return cn(
        baseClassName,
        "border border-[#2144FF] bg-white text-[#2144FF] hover:bg-[#F6F1FF] active:border-[#132EDB] active:text-[#132EDB]",
      );
    case "ghost":
      return cn(baseClassName, "bg-transparent text-[#2144FF] hover:bg-[#F6F1FF]");
    case "link":
      return cn(
        baseClassName,
        "min-h-10 px-1 py-2 text-[#2144FF] underline-offset-4 hover:underline",
      );
    case "primary":
      return cn(
        baseClassName,
        "bg-[#2144FF] text-white shadow-[0_14px_34px_rgba(33,68,255,0.24)] hover:bg-[#132EDB] active:bg-[#0F25B8] motion-safe:hover:-translate-y-0.5",
      );
    default:
      return baseClassName;
  }
};

export const isDropdownItem = (item: MegaMenuItem): item is MegaMenuDropdown & MegaMenuItem =>
  getItemKind(item) === "dropdown";

export const isGroupItem = (item: MegaMenuItem): item is MegaMenuGroup & MegaMenuItem => {
  const itemKind = getItemKind(item);

  return itemKind === "group";
};

export const hasDropdownContent = (item: MegaMenuDropdown) => (item.links?.length ?? 0) > 0;

export const hasGroupContent = (item: MegaMenuGroup) =>
  (item.groups ?? []).some((entry) => (entry.links?.length ?? 0) > 0);

const getNavigationItemHrefs = (item: MegaMenuItem): string[] => {
  if (isDropdownItem(item)) {
    return item.links.map((link) => link.href);
  }

  if (isGroupItem(item)) {
    return (item.groups ?? []).flatMap((group) => group.links.map((link) => link.href));
  }

  return [(item as MegaMenuLink).href];
};

export const getKaajActiveNavigationItemIndex = (
  items: MegaMenuItem[],
  currentPath: string | null | undefined,
): number | undefined => {
  let activeIndex: number | undefined;
  let activeSpecificity = -1;

  items.forEach((item, index) => {
    getNavigationItemHrefs(item).forEach((href) => {
      if (!getKaajPathMatch(currentPath, href)) return;

      const specificity = normalizeKaajPath(href)?.length ?? -1;
      if (specificity > activeSpecificity) {
        activeIndex = index;
        activeSpecificity = specificity;
      }
    });
  });

  return activeIndex;
};
